import { ModeEditOutlined as ModeEditOutlinedIcon } from 'components/icons';
import Big from 'big.js';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import LabelledData from 'pages/timecards/LabelledData';
import { useMemo, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useSelector } from 'store';
import { FeatureFlag } from 'types/features';
import { InvoiceExpense, InvoiceMatterForm } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { paymentCurrencyInputClassName } from 'utils/usePaymentCurrencyMode';
import { InvoiceDetailsParameterizedTracker } from '../../InvoiceDetailsChangeTrackers';
import { getInvoiceExpenseAmountPattern } from '../invoiceExpenseAmountPattern';

type Props = {
    isRowDisabled: boolean;
    readonly: boolean;
    index: number;
    control: Control<InvoiceMatterForm>;
    onSubmit: () => void;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    invoiceExpense: InvoiceExpense;
    advancedBillingEnabled: boolean;
    isPaymentCurrencyMode?: boolean;
    displayCurrency?: string;
    invoice: InvoiceData;
};

const ModeEditIcon = ({ className }: { className?: string }) => (
    <ModeEditOutlinedIcon className={cn('text-grey-500', className)} fontSize="small" />
);

export const OriginalAmountDisplay = ({
    index,
    control,
    onSubmit,
    setValue,
    readonly,
    invoiceExpense,
    isRowDisabled,
    advancedBillingEnabled,
    isPaymentCurrencyMode,
    displayCurrency,
    invoice,
}: Props) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [isOriginalAmountFieldHovered, setIsOriginalAmountFieldHovered] = useState(false);
    const { expense } = invoiceExpense;
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);
    const isNegativeAmountAllowed = useIsFeatureEnabled(FeatureFlag.EXPENSES_NEGATIVE_AMOUNT_ALLOWED);

    const [originalAmount, originalCurrency, isNonBillable] = useWatch({
        control,
        name: [
            `invoiceExpenses.${index}.expenseAmount`,
            `invoiceExpenses.${index}.currencyType`,
            `invoiceExpenses.${index}.isNonBillable`,
        ],
    });

    const dispayableAmount = useMemo(
        () =>
            new Big(
                Math.round((!originalAmount || isNaN(Number(originalAmount)) ? 0 : originalAmount) * 100) / 100
            ).toFixed(2),
        [originalAmount]
    );

    const storedParam = Math.round(Number(expense?.expenseAmount ?? 0) * 100) / 100;
    const storedParamInPaymentCurrency =
        Math.round(storedParam * Number(invoice.paymentCurrencyConversionRate ?? 1) * 100) / 100;

    return (
        <div className="flex flex-col">
            {!advancedBillingEnabled && !isEditMode ? (
                <div
                    className={cn(
                        'flex cursor-pointer justify-between',
                        (readonly || isRowDisabled) && 'pointer-events-none'
                    )}
                    onMouseEnter={() => setIsOriginalAmountFieldHovered(true)}
                    onMouseLeave={() => setIsOriginalAmountFieldHovered(false)}
                    onClick={() => {
                        setIsEditMode(true);
                        setIsOriginalAmountFieldHovered(false);
                    }}
                >
                    <div className="*:flex *:flex-col">
                        <LabelledData label="invoices.details.table.amount:">
                            <span className="font-semibold whitespace-nowrap">
                                {`${dispayableAmount} ${displayCurrency || originalCurrency?.label || ''}`}
                            </span>
                        </LabelledData>
                    </div>
                    {isOriginalAmountFieldHovered ? <ModeEditIcon /> : null}
                </div>
            ) : (
                <div className="flex flex-row flex-wrap gap-2">
                    <DecimalInput
                        gap={advancedBillingEnabled ? 1 : 0}
                        wrapperClassName={cn('max-w-[100px]', isPaymentCurrencyMode && paymentCurrencyInputClassName)}
                        pattern={getInvoiceExpenseAmountPattern(isNegativeAmountAllowed)}
                        control={control}
                        name={`invoiceExpenses.${index}.expenseAmount`}
                        label={'invoices.details.table.amount'}
                        onBlur={() => {
                            const compareAmount = isPaymentCurrencyMode
                                ? invoiceExpense.expenseAmountInPaymentCurrency
                                : invoiceExpense.expenseAmount;
                            // A lone minus sign passes the input pattern while typing but is not an amount
                            const isCompleteAmount = !!originalAmount && Number.isFinite(Number(originalAmount));
                            if (isCompleteAmount && originalAmount !== compareAmount) {
                                onSubmit();
                            } else if (!isCompleteAmount) {
                                setValue(`invoiceExpenses.${index}.expenseAmount`, compareAmount);
                            }
                            setIsEditMode(false);
                        }}
                        readonly={readonly}
                        disabled={isNonBillable || areButtonsDisabled}
                        autoFocus
                        suffixComponent={
                            advancedBillingEnabled ? undefined : (
                                <ModeEditIcon className="absolute top-[5px] right-[5px] size-5" />
                            )
                        }
                    />
                    {advancedBillingEnabled ? (
                        <div className="flex items-end">{displayCurrency || originalCurrency?.label || ''}</div>
                    ) : null}
                </div>
            )}
            <InvoiceDetailsParameterizedTracker
                currentParam={Number(originalAmount ?? 0)}
                displayValue={
                    isPaymentCurrencyMode
                        ? `${storedParamInPaymentCurrency} ${displayCurrency}`
                        : `${storedParam} ${originalCurrency?.label || ''}`
                }
                storedParam={isPaymentCurrencyMode ? storedParamInPaymentCurrency : storedParam}
                textWrap="nowrap"
            />
        </div>
    );
};
